/**
 * Line icons of the "estilo ML" redesign (Figma "Instoque — Telas", Esboço pages). The Figma
 * draws each one as a stroked SVG on a 24 grid scaled to its slot (14–40 px); they are kept here
 * as path data on that grid and painted with `currentColor`, the same way `NavIcon` and
 * `CategoryIcon` already are, so one glyph follows the red header, the light page and the dark
 * mode without three files.
 *
 * Decorative by default: the label next to an icon carries its meaning. Pass `label` only where
 * the icon is the whole control's content and no visible text says what it does.
 */
const GLYPHS = {
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM20 20l-3.5-3.5',
  pin: 'M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11zM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  chevronDown: 'M6 9l6 6 6-6',
  chevronUp: 'M6 15l6-6 6 6',
  chevronRight: 'M9 6l6 6-6 6',
  chevronLeft: 'M15 6l-6 6 6 6',
  bell: 'M10 20a2 2 0 0 0 4 0M6 16v-5a6 6 0 0 1 12 0v5l1.5 2h-15z',
  cart: 'M3 4h2l2.4 11h10.2L20 8H6.2M9 20.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM17 20.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
  menu: 'M4 7h16M4 12h16M4 17h16',
  clock: 'M12 7v5l3 2M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z',
  help: 'M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6v.6M12 17.5h.01M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z',
  repeat: 'M20 11a8 8 0 0 0-14.3-4.9L4 8M4 4v4h4M4 13a8 8 0 0 0 14.3 4.9L20 16M20 20v-4h-4',
  contract: 'M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h4',
  barcode: 'M4 5v14M7 5v14M10 5v14M12 5v14M15 5v14M17 5v14M20 5v14',
  table: 'M4 4h16v16H4zM4 10h16M10 10v10',
  pencil: 'M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  star: 'M12 3l2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-2.9-5.6 2.9 1.1-6.2L3 9.6l6.2-.9z',
  tag: 'M3 12V4h8l10 10-8 8zM7.5 8.5h.01',
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  home: 'M4 11l8-7 8 7v9h-5v-6H9v6H4z',
  receipt: 'M7 3h10a1 1 0 0 1 1 1v17l-3-2-3 2-3-2-3 2V4a1 1 0 0 1 1-1zM9 8h6M9 12h6',
  chart: 'M4 19h16M6 15l4-4 3 3 5-6',
  truck:
    'M3 7h11v9H3zM14 10h4l3 3v3h-7M7 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM17 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
  building: 'M4 21V8l5 3V8l5 3V4h6v17zM8 16h1M12 16h1M16 16h1',
  logout: 'M10 4H5v16h5M14 8l4 4-4 4M18 12H9',
  close: 'M6 6l12 12M18 6L6 18',
  shield: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6zM9 12l2 2 4-4',
  arrowRight: 'M5 12h14M13 6l6 6-6 6',
  check: 'M5 12l5 5 9-10',
  alert: 'M12 4l9 16H3zM12 10v4M12 17h.01',
  list: 'M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01',
  sort: 'M7 4v16M4 7l3-3 3 3M17 20V4M14 17l3 3 3-3',
  copy: 'M9 9h11v11H9zM5 15H4V4h11v1',
  download: 'M12 4v11M7 10l5 5 5-5M5 20h14',
  trash: 'M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0',
  users:
    'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21a7 7 0 0 1 14 0M16 3.5a4 4 0 0 1 0 7.5M18 14a6 6 0 0 1 4 7',
  lock: 'M6 11h12v10H6zM8 11V7a4 4 0 0 1 8 0v4',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 11v5M12 8h.01',
  box: 'M4 8l8-4 8 4-8 4zM4 8v8l8 4 8-4V8M12 12v8',
  external: 'M14 4h6v6M20 4l-9 9M18 14v6H4V6h6',
  filter: 'M4 5h16l-6 8v6l-4-2v-4z',
} as const;

export type IconName = keyof typeof GLYPHS;

export interface IconProps {
  readonly name: IconName;
  /** Rendered size in px; the Figma slot (14, 16, 18, 20, 22, 24…). */
  readonly size?: number | undefined;
  /** Stroke on the 24 grid. The Figma uses 1.5–1.8 depending on the slot. */
  readonly strokeWidth?: number | undefined;
  /** Accessible name, for an icon that is a control's only content. */
  readonly label?: string | undefined;
  readonly className?: string | undefined;
}

export function Icon({ name, size = 18, strokeWidth = 1.8, label, className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...(label === undefined ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label })}
    >
      <path d={GLYPHS[name]} />
    </svg>
  );
}
